import { DmpConnectorSettings, SecurityLabelEnum } from '@domain/types';
import { BannerCard, Form, Radio, RadioChangeEvent, Typography } from '@honestica/ui-kit/src';
import { tracking } from '@services/analytics';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DmpSecretConnectionForm.module.less';

type DmpSecretConnectionFormProps = {
  originalSettings?: DmpConnectorSettings | undefined;
  settings: DmpConnectorSettings;
  setSettings: Dispatch<SetStateAction<DmpConnectorSettings>>;
  isPatientMinor: boolean;
  isReadOnly: boolean;
};

const option = {
  ok: 'ok',
  ko: 'ko',
};

const computeSecretConnectionNoticeVisibility = (
  originalSettings: DmpConnectorSettings | undefined,
  settings: DmpConnectorSettings,
) => {
  if (!originalSettings?.secretConnection) return false;

  return !settings.secretConnection;
};

function DmpSecretConnectionForm({
  settings,
  setSettings,
  isReadOnly,
  originalSettings,
}: DmpSecretConnectionFormProps) {
  const { t } = useTranslation();

  const updatesecretConnection = (e: RadioChangeEvent) => {
    setSettings((s) => {
      const securityLabels: Set<SecurityLabelEnum> = new Set(s.securityLabels);
      if (e.target.value === option.ok) {
        securityLabels.add(SecurityLabelEnum.HideForParents);
      }
      tracking.trackSecretConnectionChanged(e.target.value === option.ok);
      return {
        ...s,
        secretConnection: e.target.value === option.ok,
        securityLabels: Array.from(securityLabels),
      };
    });
  };

  const displaySecretConnectionNotice = computeSecretConnectionNoticeVisibility(
    originalSettings,
    settings,
  );

  return (
    <div className={styles.container}>
      <Typography.Title level={5}>
        {t('documents.integrations.dmp.secretConnectionModal.subtitle')}
      </Typography.Title>
      <Form.Item
        className={styles.formItem}
        htmlFor="secretConnection"
        label={t('documents.integrations.dmp.secretConnectionModal.label')}
      >
        <Radio.Group
          id="secretConnection"
          value={settings.secretConnection ? option.ok : option.ko}
          onChange={updatesecretConnection}
          disabled={isReadOnly}
          className={styles.radioGroup}
        >
          <Radio value={option.ok} key={option.ok} aria-label={option.ok}>
            {t(`documents.integrations.dmp.secretConnectionModal.options.${option.ok}`)}
          </Radio>
          <Radio value={option.ko} key={option.ko} aria-label={option.ko}>
            {t(`documents.integrations.dmp.secretConnectionModal.options.${option.ko}`)}
          </Radio>
        </Radio.Group>
      </Form.Item>
      {displaySecretConnectionNotice ? (
        <BannerCard
          className={styles.secretConnectionNoticeWrapper}
          type="warning"
          iconName="RoadSignWarning"
          title={t('documents.integrations.dmp.notices.secretConnection.title')}
          message={t('documents.integrations.dmp.notices.secretConnection.message')}
        />
      ) : (
        <BannerCard
          type="info"
          iconName="CircleInformation"
          message={t('documents.integrations.dmp.secretConnectionModal.info.content')}
        />
      )}
    </div>
  );
}

export { DmpSecretConnectionForm };
